/**
 * "Document model" do template Manutenção Preventiva SDAI — mesma fonte de
 * dados usada por `sections/Content.js` (PDF), extraída para um array de
 * blocos tipados e independente de React/JSX, consumido por
 * `_shared/generateDocx.js` para gerar o .docx.
 *
 * Diferente dos outros 4 templates, este não tem "Sumário"/capa isolada com
 * page-break explícito no PDF, e os parágrafos de cada seção mostram só o
 * valor da resposta (sem o nome da pergunta em negrito) — mantido assim aqui
 * para não divergir de `Content.js`.
 */
import { findReplyByQuestionId } from '../../_helpers/pdfHelpers'
import { imageGridBlocks } from '../../_shared/blockHelpers'
import { MONTHS } from '../../constants/dates'

const valueParagraph = value => ({ type: 'paragraph', text: value || 'N/A' })

export const buildDocumentModel = serviceOrder => {
  const { replies } = serviceOrder || {}

  const question359 = findReplyByQuestionId(replies, 359)
  const question363 = findReplyByQuestionId(replies, 363)
  const question365 = findReplyByQuestionId(replies, 365)
  const question366 = findReplyByQuestionId(replies, 366)
  const question367 = findReplyByQuestionId(replies, 367)
  const question368 = findReplyByQuestionId(replies, 368)
  const question369 = findReplyByQuestionId(replies, 369)
  const question370 = findReplyByQuestionId(replies, 370)
  const question371 = findReplyByQuestionId(replies, 371)
  const question373 = findReplyByQuestionId(replies, 373)
  const question375 = findReplyByQuestionId(replies, 375)
  const question379 = findReplyByQuestionId(replies, 379)
  const question380 = findReplyByQuestionId(replies, 380)
  const question383 = findReplyByQuestionId(replies, 383)
  const question384 = findReplyByQuestionId(replies, 384)
  const question385 = findReplyByQuestionId(replies, 385)
  const question386 = findReplyByQuestionId(replies, 386)
  const question387 = findReplyByQuestionId(replies, 387)

  // `Content.js` usa `new Date()` (data atual) em vez de `endDate` para o mês/ano
  // da capa deste template especificamente — mantido igual, mesmo parecendo
  // uma inconsistência com os outros 4 templates (fora de escopo corrigir).
  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()

  const blocks = [
    {
      type: 'coverPage',
      lines: [
        `RELATÓRIO DE MANUTENÇÃO PREVENTIVA - ${MONTHS[currentMonth]?.toUpperCase()} - ${currentYear}`,
        'SDAI (SISTEMA DE DETECÇÃO E ALARME DE INCÊNDIO)',
        'SABIC – INNOVATIVE PLASTICS',
        'CENTRAL – PRÉDIO RESINAS'
      ]
    },
    { type: 'pageBreak' },

    { type: 'heading', level: 1, text: '1. Introdução' },
    {
      type: 'paragraph',
      text: 'Este Relatório tem como objetivo expressar as atividades realizadas pela empresa Easy Solutions, bem como substituição de dispositivos danificados, visando informar sempre as melhorias cabíveis ao sistema de acordo com a necessidade de cada cliente e atender a NBR 17240-2010 (Sistema de Detecção e Alarme de Incêndio, Projetos, Instalações e Manutenções do Sistema).'
    },

    { type: 'heading', level: 1, text: '2. Ferramentas utilizadas para Testar Detectores', pageBreakBefore: true },
    {
      type: 'paragraph',
      text: 'A Easy Solutions possui o Kit para testar Detectores Térmico, utilizam Soprador Térmico, equipamentos que atendem as Normas Internacionais de Fire Alarm NBR-17240-2010, conforme figura abaixo.'
    },
    {
      type: 'imageGrid',
      images: [
        { src: '/pdfs/easysolutions/fire_alarm.png', description: 'Figura 1 - Alarme de Incêndio' },
        { src: '/pdfs/easysolutions/soprador_termico.png', description: 'Figura 2 - Soprador Térmico' }
      ]
    },

    { type: 'heading', level: 1, text: '3. Serviços Realizados', pageBreakBefore: true },
    valueParagraph(question359?.value),
    ...imageGridBlocks(question359?.documents),
    valueParagraph([question363?.value, question365?.value].filter(Boolean).join(' e ')),
    ...imageGridBlocks([...(question363?.documents || []), ...(question365?.documents || [])]),
    valueParagraph(question366?.value),
    ...imageGridBlocks(question366?.documents),
    valueParagraph(question367?.value),
    ...imageGridBlocks(question367?.documents),

    { type: 'heading', level: 1, text: '4. Tensão da Central de Incêndio', pageBreakBefore: true },
    { type: 'paragraph', text: question368?.value || '' },
    ...imageGridBlocks(question368?.documents),

    { type: 'heading', level: 1, text: '5. Teste do Sistema', pageBreakBefore: true },
    valueParagraph(question369?.value),
    ...imageGridBlocks(question369?.documents),

    { type: 'heading', level: 1, text: '6.', pageBreakBefore: true },
    valueParagraph(question370?.value),
    ...imageGridBlocks(question370?.documents),
    valueParagraph(question373?.value),
    ...imageGridBlocks(question373?.documents),
    valueParagraph(question375?.value),
    ...imageGridBlocks(question375?.documents),
    valueParagraph([question379?.value, question380?.value].filter(Boolean).join(' e ')),
    ...imageGridBlocks([...(question379?.documents || []), ...(question380?.documents || [])]),

    { type: 'heading', level: 1, text: '7. Status do sistema de supressão por CO2 ao sair', pageBreakBefore: true },
    valueParagraph(question371?.value),
    ...imageGridBlocks(question371?.documents),
    valueParagraph(question383?.value),
    ...imageGridBlocks(question383?.documents),
    valueParagraph(question384?.value),
    ...imageGridBlocks(question384?.documents),
    valueParagraph(question385?.value),
    ...imageGridBlocks(question385?.documents),

    { type: 'heading', level: 1, text: '8. Melhoria no sistema', pageBreakBefore: true },
    valueParagraph(question386?.value),
    ...imageGridBlocks(question386?.documents),

    { type: 'heading', level: 1, text: '9. Conclusão', pageBreakBefore: true },
    valueParagraph(question387?.value),
    ...imageGridBlocks(question387?.documents)
  ]

  return {
    title: `Relatório de Manutenção Preventiva SDAI - ${MONTHS[currentMonth]} ${currentYear}`,
    blocks
  }
}

export default buildDocumentModel
